import { useEffect, useState } from 'react'
import {
  DAYS,
  DEFAULT_PREFS,
  loadPrefs,
  previewDigest,
  savePrefs,
  type NotificationPrefs,
} from '@core/notificationPrefs'
import { Button, Card, ErrorNote } from '@design/components'

/**
 * The daily summary, and the hours the app keeps quiet.
 *
 * The preview underneath is the point of the screen: a setting about a message
 * you will receive every morning should show you the message. Without it you
 * are agreeing to something you have never read.
 */
export function DigestSetting({ userId }: { userId: string }) {
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    loadPrefs(userId)
      .then(setPrefs)
      .catch(() => setPrefs(DEFAULT_PREFS))
    previewDigest(userId)
      .then(setPreview)
      .catch(() => setPreview(null))
  }, [userId])

  if (!prefs) return null

  const set = (patch: Partial<NotificationPrefs>) => {
    setPrefs({ ...prefs, ...patch })
    setSaved(false)
  }

  async function save() {
    setBusy(true)
    setError('')
    try {
      await savePrefs(userId, prefs!)
      setSaved(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan lukte niet.')
    }
    setBusy(false)
  }

  return (
    <Card className="mb-4">
      <p className="font-medium mb-1">Dagelijks bericht</p>
      <p className="text-sm text-muted mb-3">
        Eén bericht per ochtend, ook op een dag dat er niets is — anders zegt stilte
        niets.
      </p>

      <label className="flex items-center justify-between gap-3 py-2">
        <span className="text-sm">Stuur het dagelijks bericht</span>
        <input
          type="checkbox"
          checked={prefs.digestOn}
          onChange={(e) => set({ digestOn: e.target.checked })}
          className="w-6 h-6"
        />
      </label>

      {prefs.digestOn && (
        <label className="flex items-center justify-between gap-3 py-2">
          <span className="text-sm">Hoe laat</span>
          <input
            type="time"
            value={prefs.digestAt}
            onChange={(e) => set({ digestAt: e.target.value })}
            className="min-h-[2.75rem] px-3 rounded-xl bg-canvas border border-line"
          />
        </label>
      )}

      <label className="block py-2">
        <span className="block text-sm mb-1.5">
          Vaste betaaldag
          <span className="block text-xs text-muted">
            Facturen betalen wanneer je eraan denkt betekent: wanneer je eraan
            denkt. Op deze dag staat het bovenaan je ochtendbericht.
          </span>
        </span>
        <select
          value={prefs.runDay === null ? '' : String(prefs.runDay)}
          onChange={(e) =>
            set({ runDay: e.target.value === '' ? null : Number(e.target.value) })
          }
          className="w-full min-h-[3rem] px-3 rounded-xl bg-canvas border border-line"
        >
          <option value="">Geen vaste dag</option>
          {DAYS.map((d) => (
            <option key={d.value} value={d.value}>
              Elke {d.label}
            </option>
          ))}
        </select>
      </label>

      <label className="flex items-center justify-between gap-3 py-2">
        <span className="text-sm">
          Zondagavond een terugblik
          <span className="block text-xs text-muted">
            Wat er deze week af is, en wat er aankomt.
          </span>
        </span>
        <input
          type="checkbox"
          checked={prefs.weeklyOn}
          onChange={(e) => set({ weeklyOn: e.target.checked })}
          className="w-6 h-6 shrink-0"
        />
      </label>

      <div className="border-t border-line mt-2 pt-2">
        <p className="text-sm mb-1">Niet storen</p>
        <div className="flex items-center gap-2">
          <input
            type="time"
            value={prefs.quietFrom}
            onChange={(e) => set({ quietFrom: e.target.value })}
            className="flex-1 min-h-[2.75rem] px-3 rounded-xl bg-canvas border border-line"
          />
          <span className="text-muted text-sm">tot</span>
          <input
            type="time"
            value={prefs.quietUntil}
            onChange={(e) => set({ quietUntil: e.target.value })}
            className="flex-1 min-h-[2.75rem] px-3 rounded-xl bg-canvas border border-line"
          />
        </div>
      </div>

      {preview && (
        <div className="mt-3 p-3 rounded-xl bg-canvas border border-line">
          <p className="text-xs text-muted mb-1">Zo ziet het er vandaag uit</p>
          <p className="font-medium text-sm">Vandaag</p>
          <p className="text-sm text-muted">{preview}</p>
        </div>
      )}

      <ErrorNote>{error}</ErrorNote>

      <div className="mt-3">
        <Button tone="secondary" onClick={save} disabled={busy}>
          {busy ? 'Bezig…' : saved ? 'Opgeslagen' : 'Opslaan'}
        </Button>
      </div>
    </Card>
  )
}
